<template>
  <div ref="pageRoot" class="tracking-system">
    <SearchCard v-model="queryForm" @handleQuery="handleQuery"></SearchCard>
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 左侧人员列表 -->
      <aside class="people-list">
        <div class="stats-row">
          <div class="stat-cell">
            <div class="stat-value green" style="color: #22C55E;">{{ statisticsData.onlineDeviceCount }}</div>
            <div class="stat-label">在线人数</div>
          </div>
          <div class="stat-cell">
            <div class="stat-value gray" style="color: #9CA3AF;">{{ statisticsData.offlineDeviceCount }}</div>
            <div class="stat-label">离线人数</div>
          </div>
          <div class="stat-cell">
            <div class="stat-value blue">{{ statisticsData.busyEmployeeCount }}</div>
            <div class="stat-label">任务中</div>
          </div>
          <div class="stat-cell">
            <div class="stat-value blue">{{ statisticsData.idleEmployeeCount }}</div>
            <div class="stat-label">空闲</div>
          </div>
        </div>
        <div class="people-list-scroll">
          <div
            v-for="(item,index) in pageData"
            :key="item.id || item.employeeId || item.rtkId || index"
            class="list-item"
            :class="{active: active === index}"
            @click="selectPerson(item,index)"
          >
            <div class="status-dot online" :class="{offline: !isOnline(item)}"></div>
            <div class="user-info">
              <div class="name-id">
                <span class="name">{{ item.employeeName || '未命名人员' }}</span>
                <span class="id">/{{ item.employeeId || item.rtkId || '—' }}</span>
              </div>
              <div class="task">状态: {{ isBusy(item) ? '任务中' : '空闲' }}</div>
              <div class="progress-bar">
                <!-- <el-progress :percentage="85" stroke-width="4" class="progress" /> -->
              </div>
            </div>
            <div class="time">{{ formatOffline(item) }}</div>
          </div>
        </div>
      </aside>

      <section class="map-panel">
        <TiandiMap
          ref="map"
          :persons="mapPoints"
          :active-person-id="activePointId"
          :auto-fit="mapPoints.length > 0"
          height="100%"
          :show-toolbar="false"
          @person-click="handleMapPointClick"
        ></TiandiMap>
      </section>
    </div>

    <div class="footer">
      <span>系统状态：正常运行</span>
      <span>最后刷新：{{ lastRefreshTime || '—' }}</span>
      <span>在线率：{{ onlineRate }}</span>
      <span :class="{ paused: !autoRefresh }">自动刷新：{{ autoRefresh ? `${refreshSeconds}s` : '已暂停' }}</span>
    </div>

    <UserInfoDialog ref="userDialog" />
  </div>
</template>

<script>

import SearchCard from "./components/search-card";
import UserInfoDialog from "./components/user-info-dialog";
import { getRealtimeRtkStaffPage } from "./api";

import TiandiMap from '@/components/tiandi-map/index.vue'

export default {
  name: 'PersonnelTrajectory',
  components: {
    SearchCard,
    UserInfoDialog,
    TiandiMap
  },
  data () {
    return {
      loading: false,
      active: -1,
      pageData: [],
      queryForm: {
        page: 1,
        size: 200
      },
      autoRefresh: true,
      refreshSeconds: 30,
      refreshTimer: null,
      lastRefreshTime: '',
      statisticsData: {
        onlineDeviceCount: 0,
        offlineDeviceCount: 0,
        busyEmployeeCount: 0,
        idleEmployeeCount: 0
      }
    }
  },
  computed: {
    summary () {
      const total = this.pageData.length
      const online = this.pageData.filter(item => this.isOnline(item)).length
      const busy = this.pageData.filter(item => this.isBusy(item)).length
      return {
        total,
        online,
        offline: total - online,
        busy,
        idle: total - busy
      }
    },
    onlineRate () {
      if (!this.summary.total) return '0%'
      return `${((this.summary.online / this.summary.total) * 100).toFixed(1)}%`
    },
    mapPoints () {
      return this.pageData
        .filter(item => this.isValidCoord(item.longitude, item.latitude))
        .map(item => ({
          id: String(item.id || item.employeeId || item.rtkId),
          name: item.employeeName,
          rtkId: item.rtkId || String(item.deviceId || ''),
          lng: Number(item.longitude),
          lat: Number(item.latitude),
          status: this.isOnline(item) ? 'online' : 'offline',
          online: this.isOnline(item),
          inInspection: this.isBusy(item),
          reportTime: item.reportTime,
          raw: item
        }))
    },
    activePointId () {
      const item = this.pageData[this.active]
      return item ? String(item.id || item.employeeId || item.rtkId) : ''
    }
  },
  mounted () {
    this._layoutOnResize = () => this.syncLayoutHeight()
    window.addEventListener('resize', this._layoutOnResize)
    this.$nextTick(() => {
      this.syncLayoutHeight()
      const shell = typeof document !== 'undefined' ? document.getElementById('avue-view') : null
      if (shell && typeof ResizeObserver !== 'undefined') {
        this._layoutRo = new ResizeObserver(() => this.syncLayoutHeight())
        this._layoutRo.observe(shell)
      }
      this.init()
    })
  },
  beforeDestroy () {
    this.stopAutoRefresh()
    if (this._layoutOnResize) {
      window.removeEventListener('resize', this._layoutOnResize)
      this._layoutOnResize = null
    }
    if (this._layoutRo) {
      try {
        this._layoutRo.disconnect()
      } catch (e) {
        console.warn('实时定位页面布局监听销毁失败', e)
      }
      this._layoutRo = null
    }
    const root = this.$refs.pageRoot
    if (root && root.style) {
      root.style.height = ''
    }
  },
  methods: {
    /** 与 #avue-view 内容区等高，避免整页超出主区域产生外层滚动（与 personnel-trajectory-analysis 一致） */
    syncLayoutHeight () {
      this.$nextTick(() => {
        const shell = typeof document !== 'undefined' ? document.getElementById('avue-view') : null
        const el = this.$refs.pageRoot
        if (!shell || !el) return
        const cs = window.getComputedStyle(shell)
        const pt = parseFloat(cs.paddingTop) || 0
        const pb = parseFloat(cs.paddingBottom) || 0
        const h = Math.max(200, shell.clientHeight - pt - pb)
        el.style.height = `${h}px`
      })
    },
    init (queryForm = {}) {
      this.loadData(queryForm)
      this.startAutoRefresh()
    },
    loadData (queryForm) {
      const params = this.normalizeParams(queryForm || this.queryForm)
      this.loading = true
      return getRealtimeRtkStaffPage(params).then(({ data }) => {
        const payload = data && data.data ? data.data : data || {}
        const pageData = payload.pageData || payload.page || payload
        const records = Array.isArray(pageData.records) ? pageData.records : []
        this.pageData = records
        this.statisticsData = this.normalizeStatistics(payload.statistics, records)
        this.lastRefreshTime = this.formatNow()
        this.$nextTick(() => this.syncLayoutHeight())
      }).catch(() => {
        this.$message.error('实时定位数据加载失败')
      }).finally(() => {
        this.loading = false
      })
    },
    normalizeParams (queryForm) {
      const params = {
        ...(queryForm || {}),
        page: 1,
        size: 200
      }
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] == null) {
          delete params[key]
        }
      })
      return params
    },
    normalizeStatistics (statistics, records) {
      if (statistics) {
        return {
          onlineDeviceCount: Number(statistics.onlineDeviceCount || 0),
          offlineDeviceCount: Number(statistics.offlineDeviceCount || 0),
          busyEmployeeCount: Number(statistics.busyEmployeeCount || 0),
          idleEmployeeCount: Number(statistics.idleEmployeeCount || 0)
        }
      }
      const onlineDeviceCount = records.filter(item => this.isOnline(item)).length
      const busyEmployeeCount = records.filter(item => this.isBusy(item)).length
      return {
        onlineDeviceCount,
        offlineDeviceCount: records.length - onlineDeviceCount,
        busyEmployeeCount,
        idleEmployeeCount: records.length - busyEmployeeCount
      }
    },
    selectPerson (item, index) {
      this.active = index
      this.$refs.userDialog.open(item)
      if (this.$refs.map && item.longitude != null && item.latitude != null) {
        this.$refs.map.locatePerson(String(item.id || item.employeeId || item.rtkId))
      }
    },
    handleMapPointClick (point) {
      const idx = this.pageData.findIndex(item => String(item.id || item.employeeId || item.rtkId) === point.id)
      if (idx > -1) {
        this.selectPerson(this.pageData[idx], idx)
      }
    },
    handleQuery () {
      this.active = -1
      this.loadData(this.queryForm)
      this.restartAutoRefresh()
    },
    toggleAutoRefresh () {
      this.autoRefresh = !this.autoRefresh
      this.autoRefresh ? this.startAutoRefresh() : this.stopAutoRefresh()
    },
    startAutoRefresh () {
      this.stopAutoRefresh()
      if (!this.autoRefresh) return
      this.refreshTimer = setInterval(() => {
        this.loadData()
      }, this.refreshSeconds * 1000)
    },
    stopAutoRefresh () {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    restartAutoRefresh () {
      if (this.autoRefresh) {
        this.startAutoRefresh()
      }
    },
    formatOffline (item) {
      if (!item.reportTime) return '无坐标'
      if (this.isOnline(item)) return '5分钟内'
      const minutes = Number(item.offlineMinutes)
      if (Number.isNaN(minutes)) return '超时'
      if (minutes < 60) return `${minutes}分钟前`
      const hours = Math.floor(minutes / 60)
      return `${hours}小时前`
    },
    isOnline (item) {
      return item.deviceOnline === true || item.deviceOnline === 1 || item.deviceStatus === 1 || item.deviceStatus === '1'
    },
    isBusy (item) {
      return item.inInspection === true || item.inInspection === 1 || item.employeeStatus === 1 || item.employeeStatus === '1'
    },
    isValidCoord (longitude, latitude) {
      const lng = Number(longitude)
      const lat = Number(latitude)
      return !Number.isNaN(lng) && !Number.isNaN(lat)
    },
    formatNow () {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }
  }
}
</script>

<style scoped lang="scss">
.tracking-system {
  width: 100%;
  min-height: 0;
  background-color: #f5f7fa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  /* 与下方主区间距由 SearchCard 的 margin-bottom 承担 */
  gap: 0;
}

/* 主内容区域：左右列同高，内部再分区滚动 */
.main-content {
  flex: 1;
  min-height: 0;
  margin: 0 !important;
  width: 100%;
  padding: 0 0 12px;
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  overflow: hidden;
}

/* 人员列表：上统计条 + 下可滚动列表 */
.people-list {
  width: 29.1667%;
  max-width: 420px;
  min-width: 280px;
  min-height: 0;
  background-color: #fff;
  border-radius: 4px;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
  box-sizing: border-box;
}

.stats-row {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #ebeef5;
}

.stat-cell {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 4px 6px;
}

.stat-cell + .stat-cell {
  border-left: 1px solid #ebeef5;
}

.stat-cell .stat-value {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  color: #1890ff;
}

.stat-cell .stat-label {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
}

.people-list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px 10px;
}

.list-item {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) 72px;
  align-items: start;
  column-gap: 10px;
  padding: 12px 10px;
  margin-bottom: 8px;
  border-radius: 5px;
  border: 1px solid transparent;
  cursor: pointer;
  background: #fafafa;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.list-item:hover {
  background: #fff;
  border-color: #93c5fd;
}

.list-item.active {
  background: #eff6ff;
  border-color: #2563eb;
}

.status-dot {
  width: 10px;
  height: 10px;
  margin-top: 7px;
  border-radius: 50%;
}

.status-dot.online {
  background: #22c55e;
}

.status-dot.offline {
  background: #a8abb2;
}

.user-info {
  min-width: 0;
  overflow: hidden;
}

.name-id {
  display: -webkit-box;
  overflow: hidden;
  line-height: 1.35;
  word-break: break-all;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  margin-bottom: 6px;
}

.name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.id {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.task {
  font-size: 13px;
  line-height: 1.4;
  color: #303133;
}

.time {
  min-width: 0;
  padding-top: 2px;
  text-align: right;
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.4;
  color: #303133;
}


/* 地图区域：占满右侧列剩余高度 */
.map-container {
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
  box-sizing: border-box;
}

.map-container ::v-deep .gaode-map-root {
  flex: 1;
  min-height: 0;
}

.right-state {
  width: 64px;
  text-align: right;
  flex-shrink: 0;
  font-size: 13px;
}

.right-state small {
  display: block;
  margin-top: 4px;
  color: #909399;
}

.online-text {
  color: #16a34a;
  font-weight: 600;
}

.offline-text {
  color: #909399;
  font-weight: 600;
}

.map-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  overflow: hidden;
}

.footer {
  flex-shrink: 0;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 16px;
  background: #fff;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #606266;
}

.footer .paused {
  color: #e6a23c;
}

.stats-summary {
  text-align: center;
}

.warning {
  text-align: right;
  color: #faad14;
}
</style>
